<template>
  <div class="pid-page">
    <header class="pid-hero">
      <div>
        <div class="pid-kicker">APPSFLYER PARTNERS</div>
        <h1>Partners</h1>
      </div>
      <div class="pid-hero-actions">
        <button
          v-if="isAdmin"
          type="button"
          class="pid-btn pid-btn--ok"
          @click="openCreateModal"
        >
          Add partner
        </button>
        <a class="pid-back" href="/apps/script-tags/data-manager">Back to data manager</a>
      </div>
    </header>

    <section class="pid-board">
      <div class="pid-grid pid-grid--head">
        <div>ID</div>
        <div class="pid-sortable" @click="sortByField('pid_text')">Partner Name</div>
        <div>PID</div>
        <div>Subs</div>
        <div>User Name</div>
        <div>Password</div>
        <div class="pid-col-ono">O&amp;O</div>
        <div>Actions</div>
      </div>

      <div
        v-for="row of rows"
        :key="row.id"
        class="pid-grid pid-grid--row"
      >
        <div><span class="pid-id">#{{ row.id }}</span></div>

        <div>{{ row.pid_text }}</div>

        <div class="pid-mono">{{ row.pid_value }}</div>

        <div class="pid-verbals-cell">
          <div class="pid-sub-dropdown" :class="{ 'pid-sub-dropdown--empty': !hasSub(row) }">
            <i class="fa fa-caret-down pid-sub-dropdown__icon" aria-hidden="true"></i>
            <div class="pid-sub-dropdown__panel">
              <template v-if="hasSub(row)">
                <div
                  v-for="field in subFieldEntries(row)"
                  :key="field.key"
                  class="pid-sub-dropdown__item"
                >
                  <span class="pid-sub-dropdown__key">{{ field.label }}</span>
                  <span class="pid-sub-dropdown__val">{{ field.value || '—' }}</span>
                </div>
              </template>
              <div v-else class="pid-sub-dropdown__empty">Not configured</div>
            </div>
          </div>
        </div>

        <div>{{ row.user_name }}</div>

        <div class="pid-mono">{{ row.password }}</div>

        <div class="pid-col-ono">
          <span class="pid-ono-badge" :class="{ 'pid-ono-badge--on': row.ono }">
            {{ row.ono ? 'Yes' : 'No' }}
          </span>
        </div>

        <div class="pid-actions">
          <button
            v-if="isAdmin"
            class="pid-btn pid-btn--neutral"
            type="button"
            @click="openEditModal(row)"
          >
            Edit
          </button>
          <button
            v-if="isAdmin"
            class="pid-btn pid-btn--danger"
            type="button"
            @click="deleteEntity(row.id)"
          >
            Delete
          </button>
        </div>
      </div>
    </section>

    <b-modal
      v-model="partnerModal.show"
      :title="partnerModal.isCreate ? 'Add partner' : `Edit partner #${partnerModal.form.id}`"
      hide-footer
      size="lg"
      @hidden="resetPartnerModal"
    >
      <div class="pid-modal-form">
        <div class="pid-modal-section">
          <div class="pid-modal-section-title">Partner</div>

          <label class="pid-field-label">Partner Name <span class="pid-sub-required">*</span></label>
          <b-form-input
            v-model.trim="partnerModal.form.pid_text"
            class="pid-input"
            placeholder="Partner Name"
          />

          <label class="pid-field-label">PID <span class="pid-sub-required">*</span></label>
          <b-form-input
            v-model.trim="partnerModal.form.pid_value"
            class="pid-input pid-input-mono"
            placeholder="PID"
          />

          <label class="pid-field-label">User Name</label>
          <b-form-input
            v-model.trim="partnerModal.form.user_name"
            class="pid-input"
            :disabled="!userHasPermission('admin')"
            placeholder="User Name"
          />

          <label class="pid-field-label">Password</label>
          <b-form-input
            v-model.trim="partnerModal.form.password"
            class="pid-input pid-input-mono"
            :disabled="!userHasPermission('admin')"
            placeholder="Password"
          />

          <div class="pid-modal-ono">
            <b-form-checkbox switch v-model="partnerModal.form.ono">ONO</b-form-checkbox>
          </div>
        </div>

        <div class="pid-modal-section">
          <div class="pid-modal-section-title">Subs</div>

          <label class="pid-field-label">af_adset_id <span class="pid-sub-required">*</span></label>
          <b-form-input
            v-model.trim="partnerModal.form.af_adset_id"
            class="pid-input pid-input-mono"
          />

          <label class="pid-field-label">af_adset <span class="pid-sub-required">*</span></label>
          <b-form-input
            v-model.trim="partnerModal.form.af_adset"
            class="pid-input pid-input-mono"
          />

          <label class="pid-field-label">af_ad <span class="pid-sub-required">*</span></label>
          <b-form-input
            v-model.trim="partnerModal.form.af_ad"
            class="pid-input pid-input-mono"
          />

          <label class="pid-field-label">af_ad_id <span class="pid-sub-required">*</span></label>
          <b-form-input
            v-model.trim="partnerModal.form.af_ad_id"
            class="pid-input pid-input-mono"
          />
        </div>
      </div>

      <div class="pid-sub-modal-actions">
        <div class="pid-sub-modal-actions-right">
          <button
            type="button"
            class="pid-btn pid-btn--neutral"
            :disabled="partnerModal.saving"
            @click="partnerModal.show = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="pid-btn pid-btn--ok"
            :disabled="partnerModal.saving"
            @click="savePartnerModal"
          >
            <b-spinner v-if="partnerModal.saving" small class="pid-sub-modal-spinner"></b-spinner>
            Save
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
const emptyPartnerForm = () => ({
  id: 0,
  pid_text: '',
  pid_value: '',
  user_name: '',
  password: '',
  af_adset_id: '',
  af_adset: '',
  af_ad: '',
  af_ad_id: '',
  ono: false,
});

export default {
  data() {
    return {
      rows: [],
      isAdmin: true,
      sortOptions: {
        sortOrder: 'asc',
        sortByField: 'pid_text',
      },
      partnerModal: {
        show: false,
        saving: false,
        isCreate: true,
        form: emptyPartnerForm(),
      },
    };
  },
  async created() {
    const permList = await this.$store.state.user.perms;
    if (Object.values(permList).indexOf('bundles.admin') > -1) {
      this.isAdmin = true;
    }
    await this.getListData();
  },
  methods: {
    emptyPartnerForm() {
      return emptyPartnerForm();
    },
    normalizeRow(row) {
      row.af_adset_id = row.af_adset_id || '';
      row.af_adset = row.af_adset || '';
      row.af_ad = row.af_ad || '';
      row.af_ad_id = row.af_ad_id || '';
      row.ono = row.ono === 1 || row.ono === true;
      return row;
    },
    hasSub(row) {
      return Boolean(
        row &&
        (row.af_adset_id || row.af_adset || row.af_ad || row.af_ad_id)
      );
    },
    subFieldEntries(row) {
      return [
        { key: 'af_adset_id', label: 'af_adset_id', value: row.af_adset_id },
        { key: 'af_adset', label: 'af_adset', value: row.af_adset },
        { key: 'af_ad', label: 'af_ad', value: row.af_ad },
        { key: 'af_ad_id', label: 'af_ad_id', value: row.af_ad_id },
      ];
    },
    validateSubFields(row) {
      const fields = [
        { key: 'af_adset_id', label: 'af_adset_id' },
        { key: 'af_adset', label: 'af_adset' },
        { key: 'af_ad', label: 'af_ad' },
        { key: 'af_ad_id', label: 'af_ad_id' },
      ];
      const missing = fields
        .filter((field) => !String(row[field.key] || '').trim())
        .map((field) => field.label);
      if (missing.length) {
        return `Sub fields are required: ${missing.join(', ')}`;
      }
      return '';
    },
    buildPidPayload(row) {
      return {
        id: row.id,
        pid_text: row.pid_text,
        pid_value: row.pid_value,
        user_name: row.user_name,
        password: row.password,
        af_adset_id: String(row.af_adset_id || '').trim(),
        af_adset: String(row.af_adset || '').trim(),
        af_ad: String(row.af_ad || '').trim(),
        af_ad_id: String(row.af_ad_id || '').trim(),
        ono: row.ono ? 1 : 0,
      };
    },
    resetPartnerModal() {
      this.partnerModal.saving = false;
      this.partnerModal.isCreate = true;
      this.partnerModal.form = this.emptyPartnerForm();
    },
    openCreateModal() {
      this.partnerModal.isCreate = true;
      this.partnerModal.form = this.emptyPartnerForm();
      this.partnerModal.show = true;
    },
    openEditModal(row) {
      this.partnerModal.isCreate = false;
      this.partnerModal.form = {
        id: row.id,
        pid_text: row.pid_text,
        pid_value: row.pid_value,
        user_name: row.user_name,
        password: row.password,
        af_adset_id: row.af_adset_id || '',
        af_adset: row.af_adset || '',
        af_ad: row.af_ad || '',
        af_ad_id: row.af_ad_id || '',
        ono: row.ono === 1 || row.ono === true,
      };
      this.partnerModal.show = true;
    },
    async savePartnerModal() {
      const form = this.partnerModal.form;

      const validateRes = await this.validateRowData(form.pid_text, form.pid_value);
      if (validateRes !== '') {
        this.notifyError(validateRes);
        return;
      }

      const subError = this.validateSubFields(form);
      if (subError) {
        this.notifyError(subError);
        return;
      }

      this.partnerModal.saving = true;
      try {
        const payload = this.buildPidPayload(form);
        const data = await this.$http.post(this.resources.Management.savePid, payload);
        if (data.body.res !== 'OK') {
          this.notifyError(data.body.msg || 'Failed to save partner');
          return;
        }
        this.partnerModal.show = false;
        await this.getListData();
      } finally {
        this.partnerModal.saving = false;
      }
    },
    async getListData() {
      const data = await this.$http.post(this.resources.Management.getPid, {});
      this.rows = (data.body.data || []).map((row) => this.normalizeRow(row));
      this.applySort();
    },
    async deleteEntity(id) {
      const data = await this.$http.post(this.resources.Management.dmDeletePid, {
        id: parseInt(id, 10),
      });
      if (data.body.res && data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to delete partner');
        return;
      }
      await this.getListData();
    },
    async validateRowData(dm_pid_text, dm_pid_value) {
      if (dm_pid_value === '') {
        return "PID value can't be empty";
      }
      if (!this.validateStr(dm_pid_value)) {
        return 'Please enter a valid PID value';
      }
      if (dm_pid_text === '') {
        return "PID name can't be empty";
      }
      if (!this.validateStr(dm_pid_text)) {
        return 'Please enter a valid PID name';
      }
      return '';
    },
    validateStr(str) {
      const re = /([A-Za-z0-9-_]+)/;
      return re.test(str);
    },
    sortByField(fieldName) {
      if (this.sortOptions.sortByField === fieldName) {
        this.sortOptions.sortOrder = this.sortOptions.sortOrder === 'desc' ? 'asc' : 'desc';
      } else {
        this.sortOptions.sortByField = fieldName;
        this.sortOptions.sortOrder = 'asc';
      }
      this.applySort();
    },
    applySort() {
      const fieldName = this.sortOptions.sortByField;
      const nonIntFields = ['pid_text'];

      if (!nonIntFields.includes(fieldName)) {
        if (this.sortOptions.sortOrder === 'desc') {
          this.rows.sort((a, b) => (parseInt(a[fieldName], 10) < parseInt(b[fieldName], 10) ? 1 : -1));
        } else {
          this.rows.sort((a, b) => (parseInt(a[fieldName], 10) > parseInt(b[fieldName], 10) ? 1 : -1));
        }
        return;
      }

      this.rows.forEach((row) => {
        row[fieldName] = row[fieldName].trim();
      });
      if (this.sortOptions.sortOrder === 'desc') {
        this.rows.sort((a, b) => (a[fieldName].toLowerCase() < b[fieldName].toLowerCase() ? 1 : -1));
      } else {
        this.rows.sort((a, b) => (a[fieldName].toLowerCase() > b[fieldName].toLowerCase() ? 1 : -1));
      }
    },
  },
};
</script>

<style scoped>
.pid-page {
  min-height: 100%;
  padding: 24px;
  color: #0f172a;
  background: #f8fafc;
}

.pid-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 18px;
}

.pid-hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pid-kicker {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  background: rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.pid-hero h1 {
  margin: 8px 0 4px;
  font-size: 32px;
  line-height: 1.05;
  font-weight: 800;
}

.pid-back {
  color: #0ea5e9;
  text-decoration: none;
  border: 1px solid rgba(14, 165, 233, 0.28);
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(14, 165, 233, 0.08);
}

.pid-board {
  border-radius: 20px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(15, 23, 42, 0.10);
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.10);
  backdrop-filter: blur(10px);
}

.pid-grid {
  display: grid;
  grid-template-columns: 68px minmax(140px, 1fr) minmax(120px, 1fr) 56px minmax(110px, 1fr) minmax(110px, 1fr) 72px 185px;
  column-gap: 12px;
  align-items: center;
}

.pid-grid--head {
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .4px;
  text-transform: uppercase;
  padding: 8px 14px 12px;
}

.pid-sortable {
  cursor: pointer;
}

.pid-grid--row {
  margin-bottom: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.pid-grid--row:hover {
  border-color: rgba(14, 165, 233, 0.35);
  box-shadow: 0 14px 32px rgba(14, 165, 233, 0.10);
}

.pid-id {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.06);
}

.pid-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.pid-col-ono {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pid-ono-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: rgba(100, 116, 139, 0.12);
}

.pid-ono-badge--on {
  color: #047857;
  background: rgba(16, 185, 129, 0.15);
}

.pid-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pid-btn {
  border: 0;
  border-radius: 10px;
  padding: 7px 12px;
  font-weight: 700;
  color: #fff;
}

.pid-btn--ok {
  background: linear-gradient(135deg, #10b981, #22c55e);
}

.pid-btn--neutral {
  background: linear-gradient(135deg, #64748b, #475569);
}

.pid-btn--danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.pid-verbals-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.pid-sub-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pid-sub-dropdown__icon {
  font-size: 18px;
  color: #0ea5e9;
  cursor: default;
  padding: 4px 8px;
}

.pid-sub-dropdown--empty .pid-sub-dropdown__icon {
  color: #94a3b8;
}

.pid-sub-dropdown__panel {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  min-width: 220px;
  max-width: 320px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #0f172a;
  color: #f8fafc;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.28);
  text-align: left;
}

.pid-sub-dropdown__panel::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #0f172a;
}

.pid-sub-dropdown:hover .pid-sub-dropdown__panel {
  display: block;
}

.pid-sub-dropdown__item {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 8px;
  font-size: 12px;
  line-height: 1.45;
  padding: 3px 0;
}

.pid-sub-dropdown__key {
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .2px;
}

.pid-sub-dropdown__val {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  word-break: break-all;
}

.pid-sub-dropdown__empty {
  font-size: 13px;
  color: #94a3b8;
}

.pid-modal-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pid-modal-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
}

.pid-modal-section:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.pid-modal-section-title {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .4px;
  text-transform: uppercase;
  color: #475569;
}

.pid-field-label {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: .3px;
}

.pid-sub-required {
  color: #dc2626;
}

.pid-modal-ono {
  margin-top: 4px;
}

.pid-sub-modal-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
}

.pid-sub-modal-actions-right {
  display: flex;
  gap: 8px;
}

.pid-sub-modal-spinner {
  margin-right: 6px;
}

:deep(.pid-input.form-control) {
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 1);
}

:deep(.pid-input.form-control:focus) {
  border-color: rgba(14, 165, 233, 0.85);
  box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

@media (max-width: 1200px) {
  .pid-board {
    overflow-x: auto;
  }

  .pid-grid {
    min-width: 1080px;
  }
}
</style>
